import React, { useState, useRef, useEffect } from 'react';

const pad2 = (n) => String(n).padStart(2, '0');

const formatStepTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatDateOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 8.5 6.5 12 13 4.5" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="6" width="11" height="11" rx="2" />
    <path d="M13 6V4a1.5 1.5 0 0 0-1.5-1.5H4.5A1.5 1.5 0 0 0 3 4v7A1.5 1.5 0 0 0 4.5 12.5H6" />
  </svg>
);

const ShippingTracker = ({ timeline, expectedDeliveryDate, trackingNumber }) => {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  if (!Array.isArray(timeline) || timeline.length === 0) return null;

  const stepCount = timeline.length;
  const lastStep = timeline[stepCount - 1].step;
  const reachedCount = timeline.filter((s) => s.reached).length;
  // Rail spans 80% of the row width (10% inset each side); fill covers the
  // gaps between reached circles.
  const fillPercent =
    reachedCount > 1 && stepCount > 1 ? ((reachedCount - 1) / (stepCount - 1)) * 80 : 0;

  const deliveredStep = timeline.find((s) => s.step === lastStep && s.reached);
  const isDelivered = !!deliveredStep;
  const currentStep = timeline.find((s) => s.current) || null;
  const currentHint = currentStep ? currentStep.hint || null : null;

  // The DATE is the headline of the card — it answers the customer's real
  // question ("when does it arrive"); the label above it is a quiet eyebrow.
  let summaryLabel = null;
  let summaryDate = null;
  if (isDelivered && deliveredStep.timestamp) {
    summaryLabel = 'נמסרה ב-';
    summaryDate = formatDateTime(deliveredStep.timestamp);
  } else if (!isDelivered && expectedDeliveryDate) {
    summaryLabel = 'צפי הגעה משוער';
    summaryDate = formatDateOnly(expectedDeliveryDate);
  }
  const summaryLine = !!summaryDate;

  const handleCopy = async () => {
    if (!trackingNumber) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(trackingNumber);
      } else {
        const ta = document.createElement('textarea');
        ta.value = trackingNumber;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Silent — copy is non-critical, the number is still visible.
    }
  };

  const showHeader = !!currentStep || !!summaryLine || !!trackingNumber;

  return (
    <section className="pa-detail__tracker" aria-label="מעקב משלוח">
      {showHeader && (
        <header className="pa-tracker__header">
          {/* The current stage IS the headline (desktop); on mobile the bold
              beacon step inside the list carries it, so CSS hides this. */}
          {currentStep && (
            <h3 className="pa-tracker__stage-title">
              <span className="pa-tracker__beacon" aria-hidden="true" />
              {currentStep.label}
            </h3>
          )}
          <div className="pa-tracker__header-row">
          {summaryLine && (
            <p className={`pa-tracker__summary ${isDelivered ? 'pa-tracker__summary--done' : ''}`}>
              <span className="pa-tracker__summary-label">{summaryLabel}</span>
              <strong className="pa-tracker__summary-date" dir="ltr">{summaryDate}</strong>
            </p>
          )}
          {trackingNumber && (
            <div className="pa-tracker__tracking">
              <span className="pa-tracker__tracking-label">מספר מעקב</span>
              <span className="pa-tracker__tracking-value" dir="ltr">{trackingNumber}</span>
              <button
                type="button"
                className="pa-tracker__copy-btn"
                onClick={handleCopy}
                aria-label="העתקת מספר מעקב"
              >
                <CopyIcon />
              </button>
              <span
                className={`pa-tracker__copied ${copied ? 'pa-tracker__copied--show' : ''}`}
                aria-live="polite"
              >
                הועתק
              </span>
            </div>
          )}
          </div>
        </header>
      )}

      {/* Desktop: the explainer sits above the pills. Mobile CSS hides it in
          favor of the in-step copy — except for delivered orders (--solo),
          whose greeting has no timeline to live in. */}
      {currentHint && (
        <p className={`pa-tracker__stage-hint${isDelivered ? ' pa-tracker__stage-hint--solo' : ''}`}>
          {currentHint}
        </p>
      )}

      {/* Delivered = the story is over; no timeline, just summary + greeting */}
      {!isDelivered && (
      <ol className="pa-tracker__steps" aria-label="שלבי המשלוח">
        <span className="pa-tracker__rail" aria-hidden="true" />
        <span
          className="pa-tracker__rail-fill"
          aria-hidden="true"
          style={{ '--pa-tracker-fill': `${fillPercent}%` }}
        />
        {timeline.map((step) => {
          const stateClass = step.current
            ? 'pa-tracker__step--current'
            : step.reached
            ? 'pa-tracker__step--reached'
            : 'pa-tracker__step--pending';
          return (
            <li
              key={step.step}
              className={`pa-tracker__step ${stateClass}`}
              aria-current={step.current ? 'step' : undefined}
            >
              <span className="pa-tracker__circle" aria-hidden="true">
                {/* check = completed only; the current stage is HAPPENING —
                    it gets the white beacon dot, never a checkmark */}
                {step.current ? (
                  <span className="pa-tracker__nowdot" />
                ) : step.reached ? (
                  <CheckIcon />
                ) : (
                  <span className="pa-tracker__circle-dot" />
                )}
              </span>
              <span className="pa-tracker__text">
                <span className="pa-tracker__label">{step.label}</span>
                {/* Mobile: the explainer nests under the current label */}
                {step.current && step.hint && (
                  <span className="pa-tracker__step-hint">{step.hint}</span>
                )}
              </span>
              {step.timestamp ? (
                <time
                  className="pa-tracker__time"
                  dateTime={step.timestamp}
                  dir="ltr"
                >
                  {formatStepTime(step.timestamp)}
                </time>
              ) : (
                step.step === lastStep && !step.reached && expectedDeliveryDate && (
                  <time
                    className="pa-tracker__time pa-tracker__time--estimate"
                    dateTime={expectedDeliveryDate}
                    dir="ltr"
                  >
                    {formatDateOnly(expectedDeliveryDate)}
                  </time>
                )
              )}
            </li>
          );
        })}
      </ol>
      )}
    </section>
  );
};

export default ShippingTracker;
